import React, {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState
} from 'react'
import {Variable, VariablesRecord} from '../../common/types/variable-engine'
import {Updator} from '../../views/card-builder/editor'
import {VariableModal} from '../../views/card-builder/variables/variable-modal'
import {useVariablePaths} from './provider'

// Todo this hook should be phased out once we have transitioned to the AF card builder

type VariableModalContextType = {
  variables: VariablesRecord
  updateVariables: (
    update: VariablesRecord | Updator<VariablesRecord>,
    rename?: {from: string; to: string}
  ) => void
  providerId?: string
}

export const VariableModalContext = createContext<
  VariableModalContextType | undefined
>(undefined)

export type OpenVariableModal = (
  name: string,
  onSave?: (varName: string, variable: Variable) => void
) => void

export function useVariableModal() {
  const onVariableSave = useRef<(name: string, variable: Variable) => void>()
  const context = useContext(VariableModalContext)
  const variablePaths = useVariablePaths()
  const [modalVariable, setModalVariable] = useState<string | undefined>()

  const closeModal = useCallback(() => {
    setModalVariable(undefined)
  }, [])
  const openModal: OpenVariableModal = useCallback(
    (name: string, onSave?: (varName: string, variable: Variable) => void) => {
      setModalVariable(name)
      onVariableSave.current = onSave
    },
    []
  )
  if (!context) {
    throw new Error(
      'VariableModalContext is not available. Ensure VariableProvider is in this components parent tree '
    )
  }

  const {variables, updateVariables, providerId} = context
  const Modal = ({
    onSave,
    onChanged
  }: {
    onSave?: (varName: string, variable: Variable) => void
    onChanged?: (changed: boolean) => void
  }) => {
    return modalVariable !== undefined ? (
      <VariableModal
        providerId={providerId}
        editing={!!modalVariable}
        name={modalVariable ?? ''}
        onSave={(name: string, variable: Variable) => {
          if (onVariableSave.current) {
            onVariableSave.current(name, variable)
          } else if (onSave) {
            onSave(name, variable)
          }
          closeModal()
        }}
        onCancel={closeModal}
        updateVariables={updateVariables}
        order="up"
        variable={
          variables && modalVariable in variables
            ? variables[modalVariable]
            : {
                source: 'static',
                type: 'string',
                default: ''
              }
        }
        variables={variables}
        variablePaths={variablePaths}
        onChanged={onChanged}
      />
    ) : null
  }
  return [openModal, Modal] as const
}
